import './Content.scss';

import cn from 'classnames';
import { Field, useFormikContext } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Select from '@/common/components/atoms/Select/Select';
import TextInput from '@/common/components/atoms/TextInput/TextInput';
import LinkLikeButton from '@/common/components/blocks/LinkLikeButton/LinkLikeButton';
import MessageBanner from '@/common/components/blocks/MessageBanner/MessageBanner';
import CheckboxField from '@/common/components/molecules/CheckboxField/CheckboxField';
import { ROLES, ROUTE_SCANBAT } from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import { ReactComponent as ArrowLeftIcon } from '@/ebat/assets/icons/chevron-light-left.svg';

import { ContentDefaultProps, ContentPropTypes } from './Content.proptypes';
import useContent from './useContent';

const Content = ({ className, disabled }) => {
  const { t } = useTranslation();
  const { errors, touched, values, setFieldValue } = useFormikContext();
  const { user } = useAuth();

  const { emailsList: originalEmailsList } = useContent();

  const {
    email,
    email_confirmation: emailConfirmation,
    sms_confirmation: smsConfirmation,
  } = values;

  useEffect(() => {
    if (user?.role === ROLES.CUSTOMER_GUEST) {
      setFieldValue('email', '0');
    }
  }, [setFieldValue, user]);

  const emailsList =
    user?.role === ROLES.CUSTOMER_GUEST
      ? [{ key: t('Nouvel email'), value: '0' }]
      : originalEmailsList;

  return (
    <div className={cn('scanbat-checkout-content', className)}>
      <div className="scanbat-checkout-content__title">
        <LinkLikeButton className="back" url={ROUTE_SCANBAT}>
          <ArrowLeftIcon className="icon" />
        </LinkLikeButton>
        <h1 className="h1" data-qa-id="title-h1">
          {t('ebat:entities:scanbat.checkout.title')}
        </h1>
      </div>
      <MessageBanner className="scanbat-checkout-content__complete" type="info">
        {t('ebat:entities:scanbat.checkout.complete')}
      </MessageBanner>
      <div className="scanbat-checkout-content__confirmation confirmation">
        <p className="confirmation__title">
          {t('ebat:entities:scanbat.checkout.confirmation.title')}
        </p>
        <div className="confirmation__email">
          <Field
            as={CheckboxField}
            checked
            context="confirmation-email"
            className="confirmation__toggle"
            disabled={disabled}
            errors={
              errors.email_confirmation &&
              touched.email_confirmation && [
                t(
                  `common:messages:forms.validation.${errors.email_confirmation}`,
                ),
              ]
            }
            id="email_confirmation"
            labelContent={t(
              'ebat:entities:scanbat.checkout.confirmation.email.toggle',
            )}
            name="email_confirmation"
            type="toggle"
            value={1}
          />
          <Field
            as={Select}
            className="confirmation__select"
            context="confirmation-email-select"
            disabled={disabled || !emailConfirmation}
            errors={
              errors.email &&
              touched.email && [
                t(`common:messages:forms.validation.${errors.email}`),
              ]
            }
            id="email"
            name="email"
            options={emailsList}
            placeholder={t(
              'ebat:entities:scanbat.checkout.confirmation.email.select',
            )}
          />
          {emailConfirmation && email === '0' && (
            <Field
              as={TextInput}
              context="new-email"
              disabled={disabled}
              errors={
                errors.email &&
                touched.email && [
                  t(`common:messages:forms.validation.${errors.email}`),
                ]
              }
              id="new_email"
              name="new_email"
              required
              type="email"
            />
          )}
        </div>
        <div className="confirmation__sms">
          <Field
            as={CheckboxField}
            className="confirmation__toggle"
            disabled={disabled}
            errors={
              errors.sms_confirmation &&
              touched.sms_confirmation && [
                t(
                  `common:messages:forms.validation.${errors.sms_confirmation}`,
                ),
              ]
            }
            id="sms_confirmation"
            labelContent={t(
              'ebat:entities:scanbat.checkout.confirmation.sms.toggle',
            )}
            name="sms_confirmation"
            type="toggle"
          />
          <Field
            as={TextInput}
            disabled={disabled || !smsConfirmation}
            errors={
              errors.mobile_phone &&
              touched.mobile_phone && [
                t(`common:messages:forms.validation.${errors.mobile_phone}`),
              ]
            }
            id="sms"
            name="mobile_phone"
            type="tel"
          />
        </div>
      </div>
    </div>
  );
};

Content.defaultProps = {
  ...ContentDefaultProps,
};

Content.propTypes = {
  ...ContentPropTypes,
};

export default Content;
